<template>
  <v-card class="mt-4 ml-2 mr-2" elevation="6">
    <v-card-title>
      <h3 class="text-headline-small">Mes sections favorites</h3>
    </v-card-title>
    <v-card-text>
      <!-- Small screens: one card per section -->
      <div v-if="isCompact" class="liked-cards">
        <v-skeleton-loader v-if="loadingSections && !sortedSections.length" type="list-item-three-line@3"></v-skeleton-loader>
        <template v-else-if="!sortedSections.length">
          <v-empty-state
            icon="mdi-heart-outline"
            size="64"
            title="Aucune section favorite"
            text="Les sections aimées apparaîtront ici quand vous les ajouterez à vos favoris en appuyant sur l'icône de cœur."
            action-text="Accéder à la liste des rivières"
            to="/rivieres"
          ></v-empty-state>
        </template>
        <v-card
          v-for="item in sortedSections"
          :key="item.id"
          variant="outlined"
          :class="['liked-card', item._borderClass]"
          @click="showRiver(item)"
        >
          <div class="liked-card__body section-cell">
            <section-row-info
              :section="item"
              liked
              show-region
              class="flex-grow-1"
              @toggle-like="unlikeSection(item)"
            ></section-row-info>
            <section-flow-summary
              v-if="isMember || item.free_access === true"
              :section="item"
              class="text-right"
            ></section-flow-summary>
            <div v-else class="text-right">
              <strong id="blur">0.00</strong> <br />
              <span class="premium-hint">
                <v-icon size="x-small">mdi-lock-outline</v-icon>
                Premium
              </span>
            </div>
          </div>
        </v-card>
      </div>
      <v-data-table
        v-else
        :items="sortedSections"
        :headers="getHeaders()"
        :loading="loadingSections"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:loading>
          <v-skeleton-loader type="list-item-three-line@3"></v-skeleton-loader>
        </template>

        <template v-slot:no-data>
          <v-empty-state
            icon="mdi-heart-outline"
            size="64"
            title="Aucune section favorite"
            text="Les sections aimées apparaîtront ici quand vous les ajouterez à vos favoris en appuyant sur l'icône de cœur."
            action-text="Accéder à la liste des rivières"
            to="/rivieres"
          ></v-empty-state>
        </template>


        <template v-slot:[`item`]="{ item }">
          <tr @click="showRiver(item)">
            <td :class="item._borderClass" valign="top" class="section-info-cell section-cell">
              <section-row-info
                :section="item"
                liked
                show-region
                @toggle-like="unlikeSection(item)"
              ></section-row-info>
            </td>
            <td class="pl-0 pr-1 text-right level-info-cell" valign="top">
              <section-flow-summary v-if="isMember || item.free_access === true" :section="item"></section-flow-summary>
              <div v-else>
                <strong id="blur">0.00</strong> <br />
                <span class="premium-hint">
                  <v-icon size="x-small">mdi-lock-outline</v-icon>
                  Premium
                </span>
                <br />
                <span id="blur">42m</span>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
  import { db } from "@/firebase";
  import firebase from "firebase/compat/app";
  import { StringService } from "@/services/String.service";
  import { RiversService } from "@/services/Rivers.service";
  import { notify, notifyError, notifyWithAction } from "@/services/Notification.service";
  import { UserService } from "@/store/modules/user";
  import SectionFlowSummary from "./SectionFlowSummary.vue";
  import SectionRowInfo from "./SectionRowInfo.vue";
  import { sectionHasCamera } from "@/utils/cameras";
  import { levelTextClass } from "@/utils/levelStatus";

  const likedSectionsTableCache = new Map();
  const likedSectionsTableInFlight = new Map();

  export default {
    components: { SectionFlowSummary, SectionRowInfo },
    emits: ["liked-changed"],
    props: {
      isMember: {
        type: Boolean,
        default: false,
      },
      authUser: {
        type: Object,
        default: null,
      },
      likedIds: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        localSections: [],
        // Liked ids behind localSections, kept up to date when a section is removed here or restored
        knownLikedIds: [],
        displaySectionCache: {},
        loadingSections: true,
        headers: [
          {
            title: "Rivière / Section",
            key: "river",
            sortable: false,
            headerProps: { class: "bg-table-header" },
          },
          {
            title: "Débit",
            key: "current_data",
            sortable: false,
            width: "110px",
            align: "end",
            headerProps: { class: "bg-table-header text-right" },
          },
        ],
        headersMobile: [
          {
            title: "Rivière / Section",
            key: "river",
            sortable: false,
            headerProps: { class: "bg-table-header" },
          },
          {
            title: "Débit",
            key: "current_data",
            sortable: false,
            width: "110px",
            align: "end",
            headerProps: { class: "bg-table-header text-right" },
          },
        ],
        level_recommended: "recommended",
        level_high: "high",
        level_low: "low",
        level_unknown: "unknown",
        // Use a sentinel so the immediate watcher always runs once on mount.
        lastLikedIdsSignature: null,
      };
    },
    computed: {
      isCompact() {
        return this.$vuetify.display.smAndDown;
      },
      // By region, then river, then section
      sortedSections() {
        const compare = (a, b) => String(a || "").localeCompare(String(b || ""), "fr");
        return [...this.localSections].sort(
          (a, b) => compare(a.region, b.region) || compare(a.river, b.river) || compare(a.name, b.name),
        );
      },
    },
    methods: {
      getLikedIdsSignature(likedIds = []) {
        return [...(likedIds || [])]
          .filter(Boolean)
          .map((id) => id.toString())
          .sort()
          .join("|");
      },
      getCacheKey() {
        const uid = this.authUser?.uid || "anon";
        return `${uid}:${this.getLikedIdsSignature(this.likedIds)}`;
      },
      async fetchLikedSections({ useLoading = true, skipCache = false } = {}) {
        let requestCacheKey = null;
        if (useLoading) {
          this.loadingSections = true;
        }

        try {
          if (!this.likedIds || this.likedIds.length === 0) {
            this.localSections = [];
            this.displaySectionCache = {};
            this.loadingSections = false;
            return;
          }

          requestCacheKey = this.getCacheKey();
          if (!skipCache && likedSectionsTableCache.has(requestCacheKey)) {
            const cached = likedSectionsTableCache.get(requestCacheKey);
            this.localSections = [...cached.localSections];
            this.knownLikedIds = [...this.likedIds];
            this.displaySectionCache = { ...cached.displaySectionCache };
            this.loadingSections = false;

            // Keep cache fresh without blocking the initial paint.
            if (!likedSectionsTableInFlight.has(requestCacheKey)) {
              this.fetchLikedSections({ useLoading: false, skipCache: true }).catch((error) => {
                console.error("Error refreshing liked sections cache:", error);
              });
            }
            return;
          }

          likedSectionsTableInFlight.set(requestCacheKey, true);

          // Process liked IDs in batches of 10 (Firestore's 'in' query limit)
          const batchSize = 10;
          const batches = [];

          for (let i = 0; i < this.likedIds.length; i += batchSize) {
            batches.push(this.likedIds.slice(i, i + batchSize));
          }

          const snapshots = await Promise.all(
            batches.map((batch) =>
              db.collection("sections").where(firebase.firestore.FieldPath.documentId(), "in", batch).get(),
            ),
          );

          const cache = this.displaySectionCache || {};
          const nextCache = {};
          const allSections = [];

          snapshots.forEach((snapshot) => {
            snapshot.docs.forEach((doc) => {
              const section = { id: doc.id, ...doc.data() };
              const sectionId = section.id || section.sectionId || section.alias;
              const signature = this.getDisplaySignature(section, sectionId);
              const cached = cache[sectionId];

              if (cached && cached._displaySignature === signature) {
                nextCache[sectionId] = cached;
                allSections.push(cached);
                return;
              }

              const displaySection = this.toDisplaySection(section, sectionId);
              nextCache[sectionId] = displaySection;
              allSections.push(displaySection);
            });
          });

          this.displaySectionCache = nextCache;
          this.localSections = allSections;
          this.knownLikedIds = [...this.likedIds];
          likedSectionsTableCache.set(requestCacheKey, {
            localSections: [...allSections],
            displaySectionCache: { ...nextCache },
          });

        } catch (error) {
          console.error("Error fetching liked sections:", error);
          notifyError("Erreur lors du chargement des sections favorites");
          this.localSections = []; // Ensure localSections is set to empty array on error
        } finally {
          if (requestCacheKey) {
            likedSectionsTableInFlight.delete(requestCacheKey);
          }
          this.loadingSections = false;
        }
      },
      getHeaders() {
        return this.$vuetify.display.smAndDown ? this.headersMobile : this.headers;
      },
      // Updates the rows and the caches after a section was removed or restored here, so the parent's
      // updated likedIds don't trigger a reload of the whole list
      setLocalLikedSections(sections, likedIds) {
        this.localSections = sections;
        this.knownLikedIds = likedIds;
        this.lastLikedIdsSignature = this.getLikedIdsSignature(likedIds);
        likedSectionsTableCache.set(`${this.authUser?.uid || "anon"}:${this.lastLikedIdsSignature}`, {
          localSections: [...sections],
          displaySectionCache: { ...this.displaySectionCache },
        });
      },
      async unlikeSection(item) {
        const uid = this.authUser?.uid;
        const index = this.localSections.findIndex((section) => section.id === item.id);
        if (!uid || index === -1) {
          return;
        }

        // Remove right away; put the row back if the request fails
        const previousSections = [...this.localSections];
        const previousLikedIds = [...this.knownLikedIds];
        this.setLocalLikedSections(
          this.localSections.filter((section) => section.id !== item.id),
          this.knownLikedIds.filter((id) => id !== item.id),
        );

        try {
          await UserService.actions.unlikeSection(uid, item.id);
          this.$emit("liked-changed", { sectionId: item.id, isLiked: false });
          notifyWithAction(`${item.river} — ${item.name} retirée des favoris.`, "Annuler", () =>
            this.restoreSection(item, index),
          );
        } catch (error) {
          console.error("Error removing liked section:", error);
          this.setLocalLikedSections(previousSections, previousLikedIds);
          notifyError("Impossible de retirer cette section des favoris.");
        }
      },
      async restoreSection(item, index) {
        const uid = this.authUser?.uid;
        if (!uid || this.localSections.some((section) => section.id === item.id)) {
          return;
        }
        try {
          await UserService.actions.likeSection(uid, item.id);
          const sections = [...this.localSections];
          sections.splice(Math.min(index, sections.length), 0, item);
          this.setLocalLikedSections(sections, [...this.knownLikedIds, item.id]);
          this.$emit("liked-changed", { sectionId: item.id, isLiked: true });
          notify(`${item.river} — ${item.name} remise dans les favoris.`);
        } catch (error) {
          console.error("Error restoring liked section:", error);
          notifyError("Impossible de remettre cette section dans les favoris.");
        }
      },
      getDisplaySignature(section, sectionId) {
        return [
          sectionId,
          Boolean(section.video_info),
          Boolean(section.topo_info),
          sectionHasCamera(section),
          section.level_status || "",
          section.current_level,
          section.current_trend,
          section.level_last_update || "",
          Boolean(section.free_access),
        ].join("|");
      },
      toDisplaySection(section, sectionId) {
        const normalizedSection = {
          ...section,
          id: sectionId,
        };

        const trend = Number(normalizedSection.current_trend);
        const currentLevel = normalizedSection.current_level;
        const hasTrend = !isNaN(trend) && !(trend === 0 && currentLevel == 0);

        return {
          ...normalizedSection,
          _videoIconColor: normalizedSection.video_info ? "blue" : "icon-inactive",
          _topoIconColor: normalizedSection.topo_info ? "blue" : "icon-inactive",
          _levelColorClass: this.getColor(normalizedSection),
          _borderClass: this.getBorderColor(normalizedSection),
          _formattedCurrentLevel:
            normalizedSection.current_level === "NaN" || normalizedSection.current_level == 0
              ? ""
              : this.formatCurrentLevel(normalizedSection.current_level),
          _currentTrendDisplay: hasTrend ? Math.abs(trend) : "",
          _currentTrendSign: hasTrend ? Math.sign(trend) : 0,
          _lastUpdate: RiversService.getLastUpdate(normalizedSection.level_last_update),
          _lastUpdateDayMonth: StringService.getLastUpdateDayAndMonth(normalizedSection.level_last_update),
          _displaySignature: this.getDisplaySignature(normalizedSection, sectionId),
        };
      },
      getColor(obj) {
        return levelTextClass(obj.level_status);
      },
      getBorderColor(obj) {
        const padding = " pl-2 pr-2";
        if (this.isMember || obj.free_access) {
          if (obj.level_status === this.level_recommended) {
            return "border-recommended" + padding;
          }
          if (obj.level_status === this.level_high) {
            return "border-too-high" + padding;
          }
          if (obj.level_status === this.level_low) {
            return "border-too-low" + padding;
          }
          if (obj.level_status === this.level_unknown) {
            return "border-unknown" + padding;
          }
        }
        return "border-unknown" + padding;
      },
      formatCurrentLevel(value) {
        // Check if value is undefined or null
        if (value === undefined || value === null) {
          return "";
        }

        // Parse the value to a number
        const numValue = parseFloat(value);

        // Check if the value is NaN
        if (isNaN(numValue)) {
          return "";
        }

        // If it's 1000 or higher, return without decimals
        if (numValue >= 1000) {
          return Math.round(numValue).toString();
        }

        // If it ends with .00, remove the decimal part
        if (value.toString().endsWith(".00")) {
          return Math.floor(numValue).toString();
        }

        // Otherwise return the original value
        return value.toString();
      },
      showRiver(row) {
        var slug = row.alias;
        this.isMember || row.free_access === true
          ? this.$router.push({ name: "riviere", params: { slug: slug } })
          : "";
      },
    },
    watch: {
      likedIds: {
        immediate: true,
        handler(newLikedIds) {
          const signature = this.getLikedIdsSignature(newLikedIds);
          if (signature === this.lastLikedIdsSignature) {
            return;
          }

          this.lastLikedIdsSignature = signature;
          this.fetchLikedSections();
        },
      },
    },
  };
</script>

<style scoped>
  .header {
    display: flex;
    align-items: center;
  }

  .class-text {
    font-size: 0.85rem;
  }

  .flow-column {
    align-content: right;
  }

  .number-rapids-text {
    font-size: 0.75rem;
  }

  .rapids-class-text {
    font-size: 0.68rem;
  }

  .number-rapids-width {
    width: 70px;
  }

  .current-trend-text {
    font-size: 0.75rem;
  }

  .last-update-text {
    font-size: 0.6rem;
  }

  .margin-top {
    margin-top: 2rem;
  }

  .collapsed > .when-open,
  .not-collapsed > .when-closed {
    display: none;
  }

  .bold {
    font-weight: bold;
  }


  .liked-cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .liked-card__body {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 0;
  }

  .border-too-high {
    border-left: 4px solid rgb(var(--v-theme-level-high));
  }

  .border-recommended {
    border-left: 4px solid rgb(var(--v-theme-level-good));
  }

  .border-too-low {
    border-left: 4px solid rgb(var(--v-theme-level-low));
  }

  .border-unknown {
    border-left: 4px solid lightgrey;
  }

  #blur {
    filter: blur(3px);
    -webkit-filter: blur(3px);
  }

  /* Members-only flow: a pale gold badge, like the brand's special badges (docs/BRAND.md) */
  .premium-hint {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin: 2px 0;
    padding: 1px 8px 1px 6px;
    border-radius: 999px;
    border: 1px solid rgba(var(--v-theme-gold), 0.5);
    background: rgb(var(--v-theme-gold-tint));
    color: rgb(var(--v-theme-on-gold-tint));
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.5;
  }

  /* Mobile responsive styles */
  @media (max-width: 600px) {
    .level-info-cell {
      width: 35%;
      text-align: right !important;
    }

    .section-info-cell {
      width: 65%;
    }

    #region {
      padding: 8px;
    }

    #region .v-btn {
      margin-right: 8px;
    }
  }
</style>
